import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Plus } from 'lucide-react';
import api from '@/lib/api';
import { getApiErrorMessage, usePartnerAuthStore } from '@/stores/partnerAuthStore';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PlanFormDialog } from '@/components/plans/PlanFormDialog';
import { formatCurrencyAmount } from '@/lib/formatMoney';
import { formatSeatRange, formatTierPrice, type PricingTier, type PricingTierMeta } from '@/types/plans';

export function PlansPage() {
  const { partner } = usePartnerAuthStore();
  const isAdmin = partner?.role === 'admin';

  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [meta, setMeta] = useState<PricingTierMeta | null>(null);
  const [editing, setEditing] = useState<PricingTier | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  /**
   * Bumped from an event handler to ask for a refetch. The fetch effect keys off
   * this, which keeps the "is a fetch outstanding" question answerable during
   * render instead of needing a flag an effect switches on.
   */
  const [reloadCount, setReloadCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const isLoading = loadedCount !== reloadCount;

  useEffect(() => {
    // `active` is what makes a superseded response harmless: the cleanup runs
    // before the next fetch starts and on unmount, so a slow early response can
    // no longer repaint the page with plans the user has already moved past.
    let active = true;

    void (async () => {
      try {
        const response = await api.get('/pricing-tiers');
        if (!active) return;
        setTiers(response.data.data);
        setMeta(response.data.meta);
      } catch (error) {
        if (!active) return;
        // Without this the page reads as "no plans exist", which for a billing
        // portal is a much worse lie than an error toast.
        setTiers([]);
        setMeta(null);
        toast.error(getApiErrorMessage(error), { id: 'plans-error' });
      }
      if (active) setLoadedCount(reloadCount);
    })();

    return () => {
      active = false;
    };
  }, [reloadCount]);

  const openCreate = () => {
    setEditing(null);
    setIsDialogOpen(true);
  };

  const openEdit = (tier: PricingTier) => {
    setEditing(tier);
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Plans</h1>
          <p className="text-muted-foreground">
            Every pricing tier, including retired ones and the groups still billed on them.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add plan
          </Button>
        )}
      </div>

      {tiers.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {isLoading ? 'Loading plans.' : 'No pricing tiers exist yet.'}
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tiers.map((tier) => (
            <Card key={tier.uuid} className={tier.isActive ? undefined : 'border-dashed'}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle>{tier.name}</CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {formatTierPrice(tier, formatCurrencyAmount)}
                  </p>
                </div>
                <Badge variant={tier.isActive ? 'default' : 'secondary'}>
                  {tier.isActive ? 'Active' : 'Retired'}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Members</dt>
                    <dd className="text-right">{formatSeatRange(tier)}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Trial</dt>
                    <dd className="text-right">{tier.trialDays > 0 ? `${tier.trialDays} days` : 'None'}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Subscribed</dt>
                    <dd className="text-right tabular-nums">
                      {tier.subscriberCount} {tier.subscriberCount === 1 ? 'group' : 'groups'}
                    </dd>
                  </div>
                </dl>

                {tier.features.length > 0 && (
                  <ul className="space-y-1 text-sm">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {tier.hasSubscribers && (
                  <p className="text-xs text-muted-foreground">
                    Pricing is locked while groups are subscribed. To change it, add a new plan and retire this one.
                  </p>
                )}

                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(tier)}>
                    Edit
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isAdmin && meta && (
        <PlanFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          tier={editing}
          meta={meta}
          onSaved={() => {
            setIsDialogOpen(false);
            setReloadCount((count) => count + 1);
          }}
        />
      )}
    </div>
  );
}
